import React, { useEffect, useRef, useState } from 'react';

import {
  applyDefaultWorkflows,
  selectedWorkflowIds,
  type StopDraft,
} from '@/pages/shipments/components/direct4wStops';
import type { Direct4WStopType, ShipmentExim } from '@/services/api/shipments';
import {
  stopWorkflowsAPI,
  type WorkflowDetail,
  type WorkflowSummary,
} from '@/services/api/stopWorkflows';

/**
 * The workflow data behind a Direct-4W stop set — the ACTIVE workflows of the
 * chosen client split by stop type, the default preselection per stop, and
 * the milestone previews of whatever workflows the stops currently reference.
 *
 * Lifted out of `CreateShipment4WModal` so the UJP wizard drives the same
 * `Direct4WStopsStep` with the same loading rules. The hook OWNS the workflow
 * state but not the stops: it takes the parent's `setStops` so it can
 * re-resolve auto picks when the set or the exim direction changes (see
 * `applyDefaultWorkflows`), while the parent keeps rebuilding/prefilling the
 * stops for its own reasons.
 */

export type WorkflowsByStopType = Record<Direct4WStopType, WorkflowSummary[]>;

export const EMPTY_WORKFLOWS: WorkflowsByStopType = {
  PICKUP: [],
  DROP_OFF: [],
};

export interface UseDirect4WWorkflowsArgs {
  /** Host (modal) open — gates every fetch and resets the state on open. */
  open: boolean;
  clientID: number | null;
  exim: ShipmentExim;
  stops: StopDraft[];
  setStops: React.Dispatch<React.SetStateAction<StopDraft[]>>;
}

export interface UseDirect4WWorkflowsResult {
  workflows: WorkflowsByStopType;
  workflowsLoading: boolean;
  wfDetails: Record<string, WorkflowDetail>;
  /** Workflow ids whose preview could not be loaded (see the details effect). */
  wfDetailErrors: ReadonlySet<string>;
}

export function useDirect4WWorkflows({
  open,
  clientID,
  exim,
  stops,
  setStops,
}: UseDirect4WWorkflowsArgs): UseDirect4WWorkflowsResult {
  const [workflows, setWorkflows] =
    useState<WorkflowsByStopType>(EMPTY_WORKFLOWS);
  const [workflowsLoading, setWorkflowsLoading] = useState(false);
  const [wfDetails, setWfDetails] = useState<Record<string, WorkflowDetail>>(
    {}
  );
  const [wfDetailErrors, setWfDetailErrors] = useState<ReadonlySet<string>>(
    new Set()
  );
  const attemptedWorkflowDetails = useRef<Set<string>>(new Set());

  // Fresh state on every open so a previous session's previews/errors never
  // leak into the next form.
  useEffect(() => {
    if (!open) return;
    setWorkflows(EMPTY_WORKFLOWS);
    setWfDetails({});
    setWfDetailErrors(new Set());
    attemptedWorkflowDetails.current = new Set();
  }, [open]);

  // Load ACTIVE workflows for the client, split by stop type. Refetches when
  // the client changes. A failure leaves the pickers empty (never blocks).
  useEffect(() => {
    if (!open || clientID == null) {
      setWorkflows(EMPTY_WORKFLOWS);
      return;
    }
    let cancelled = false;
    setWorkflowsLoading(true);
    Promise.all([
      stopWorkflowsAPI.list({
        stopType: 'PICKUP',
        clientID,
        // PICKER scope: the client's own workflows plus the GLOBAL ones.
        includeGlobal: true,
        status: 'ACTIVE',
      }),
      stopWorkflowsAPI.list({
        stopType: 'DROP_OFF',
        clientID,
        includeGlobal: true,
        status: 'ACTIVE',
      }),
    ])
      .then(([pickup, dropOff]) => {
        if (cancelled) return;
        // Preselection is driven by the [workflows, exim] effect below, so the
        // default always matches the CURRENT direction — resolving it here with
        // the load-time exim would go stale the moment ops flip Export↔Import.
        setWorkflows({ PICKUP: pickup, DROP_OFF: dropOff });
      })
      .catch(() => {
        if (!cancelled) setWorkflows(EMPTY_WORKFLOWS);
      })
      .finally(() => {
        if (!cancelled) setWorkflowsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, clientID]);

  // (Re)preselect each stop's default workflow for the CURRENT exim + loaded
  // set. Runs when the workflow set arrives AND whenever ops flip the direction,
  // so an Export booking never keeps the Import default (or vice versa).
  // Manual picks are left untouched (see applyDefaultWorkflows).
  useEffect(() => {
    if (!open) return;
    setStops((prev) => applyDefaultWorkflows(prev, workflows, exim));
  }, [open, workflows, exim, setStops]);

  /**
   * Load the milestone preview for whatever workflow each stop currently has —
   * whether ops picked it, a default preselected it, or a UJP/lane prefill
   * brought it in with the stop set.
   *
   * `attempted` is a ref, and `wfDetails` is deliberately NOT a dependency: an
   * id that fails to load never lands in `wfDetails`, while setWfDetails still
   * returns a fresh object, so keying on it would re-run the effect and retry
   * the same failing request forever.
   */
  const workflowIdsKey = selectedWorkflowIds(stops).join(',');
  useEffect(() => {
    if (!open) return;
    const ids = workflowIdsKey
      .split(',')
      .filter((id) => id && !attemptedWorkflowDetails.current.has(id));
    if (!ids.length) return;

    ids.forEach((id) => attemptedWorkflowDetails.current.add(id));
    let cancelled = false;
    void Promise.all(
      ids.map((id) =>
        stopWorkflowsAPI
          .get(id)
          .then((detail) => [id, detail] as const)
          .catch(() => [id, null] as const)
      )
    ).then((pairs) => {
      if (cancelled) return;
      const loaded: Record<string, WorkflowDetail> = {};
      const failed: string[] = [];
      for (const [id, detail] of pairs) {
        if (detail) loaded[id] = detail;
        else failed.push(id);
      }
      if (Object.keys(loaded).length) {
        setWfDetails((prev) => ({ ...prev, ...loaded }));
      }
      // Without this the card sits on "Memuat preview…" forever, which reads
      // as a hang rather than as a workflow that could not be read.
      if (failed.length) {
        setWfDetailErrors((prev) => new Set([...prev, ...failed]));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, workflowIdsKey]);

  return { workflows, workflowsLoading, wfDetails, wfDetailErrors };
}
